/**
 * Sección "Recursos de tu casa" rediseñada (smoke Fase 8).
 *
 * Reemplaza la lista plana anterior que (a) repetía los recursos sin asignar
 * — aparecían en la sección de asignación Y en la grilla — y (b) titulaba
 * "Recursos asignados" una grilla que incluía los NO asignados y no decía a
 * quién pertenecía cada uno.
 *
 * Dos grupos claros, cada recurso en uno solo:
 *  1. "Sin asignar (en la bolsa)" → con el selector para asignar.
 *  2. "Asignados" → muestra a quién (residente, y patente del vehículo para
 *     los controles) + serial del llavero + estado.
 */
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import { Section, SectionHead } from '../ui/Section';
import type {
  RecursoConSuspensionRead,
  VehiculoRead,
} from '../../lib/api/types';
import type { ResidenteConPersonaRead } from '../../lib/api/residentes';
import {
  RECURSO_ESTADO_COLOR,
  RECURSO_ESTADO_LABEL,
  RECURSO_TIPO_ICONO,
  RECURSO_TIPO_LABEL,
} from '../../lib/labels';
import {
  SeccionAsignarRecursos,
  controlesEnBolsa,
  llaverosEnBolsa,
} from './AsignarRecursoBolsa';

interface MisRecursosProps {
  recursos: RecursoConSuspensionRead[];
  residentes: ResidenteConPersonaRead[];
  vehiculos: VehiculoRead[];
  onAsignado: () => void;
  onReportar?: (recurso: RecursoConSuspensionRead) => void;
  onSolicitar?: () => void;
}

export function MisRecursos({
  recursos,
  residentes,
  vehiculos,
  onAsignado,
  onReportar,
  onSolicitar,
}: MisRecursosProps) {
  const enBolsaIds = new Set([
    ...llaverosEnBolsa(recursos).map((r) => r.id),
    ...controlesEnBolsa(recursos).map((r) => r.id),
  ]);
  const asignados = recursos.filter((r) => !enBolsaIds.has(r.id));

  const nombreResidente = (id: string | null): string | null =>
    id ? (residentes.find((r) => r.id === id)?.persona_nombre_completo ?? null)
       : null;
  const patenteVehiculo = (id: string | null): string | null =>
    id ? (vehiculos.find((v) => v.id === id)?.patente ?? null) : null;

  return (
    <Section>
      <SectionHead
        title="Recursos de tu casa"
        actions={
          onSolicitar && (
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={onSolicitar}
            >
              + Solicitar recurso
            </Button>
          )
        }
      />

      {/* Grupo 1: bolsa (sin asignar) — con selector. Se auto-oculta vacío. */}
      <SeccionAsignarRecursos
        recursos={recursos}
        residentes={residentes}
        vehiculos={vehiculos}
        onAsignado={onAsignado}
      />

      {/* Grupo 2: asignados — lista compacta alineada por columnas. Los
          nombres variables no rompen la alineación (columna identificador con
          ancho fijo + nombre con truncate). */}
      {asignados.length > 0 && (
        <div className="space-y-1 mt-1">
          <p className="text-xs uppercase tracking-wide text-text-muted">
            Asignados ({asignados.length})
          </p>
          <ul className="divide-y divide-white/[0.06]">
            {asignados.map((r) => {
              const resid = nombreResidente(r.residente_actual_id);
              const pat = patenteVehiculo(r.vehiculo_id);
              const destino = [pat, resid].filter(Boolean).join(' · ');
              return (
                <li
                  key={r.id}
                  className="flex items-center gap-3 py-2 text-sm"
                >
                  <span className="shrink-0" title={RECURSO_TIPO_LABEL[r.tipo]}>
                    {RECURSO_TIPO_ICONO[r.tipo] ?? '📦'}
                  </span>
                  {/* Columna identificador + serial: ancho fijo → alinea */}
                  <span className="shrink-0 w-44 font-mono text-xs text-text-secondary truncate">
                    {r.identificador}
                    {r.serial_rfid ? (
                      <span className="text-text-muted">
                        {' '}
                        · {r.serial_rfid}
                      </span>
                    ) : null}
                  </span>
                  {/* Destino: ocupa el resto, trunca si es largo */}
                  <span className="flex-1 min-w-0 truncate text-text-primary">
                    {destino || (
                      <span className="text-text-muted">Sin asignar</span>
                    )}
                  </span>
                  <span
                    className={`shrink-0 text-xs ${RECURSO_ESTADO_COLOR[r.estado] ?? ''}`}
                  >
                    {RECURSO_ESTADO_LABEL[r.estado] ?? r.estado}
                  </span>
                  {r.estado === 'entregado' && onReportar && (
                    <button
                      type="button"
                      onClick={() => onReportar(r)}
                      className="shrink-0 text-xs text-text-muted hover:text-orange underline"
                    >
                      Reportar
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {recursos.length === 0 && (
        <EmptyState title="Aún no hay recursos en tu casa" />
      )}
    </Section>
  );
}
